import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { Toast } from '../components/ui/Toast';
import styles from './ToastContext.module.css';

type ToastType = 'success' | 'error' | 'warning' | 'info' | 'danger';

interface ToastMessage {
  id: string;
  message: string | ReactNode;
  type: ToastType;
}

interface ToastContextValue {
  showToast: (message: string | ReactNode, type: ToastType) => void;
  success: (message: string | ReactNode) => void;
  error: (message: string | ReactNode) => void;
  warning: (message: string | ReactNode) => void;
  info: (message: string | ReactNode) => void;
  danger: (message: string | ReactNode) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const showToast = useCallback((message: string | ReactNode, type: ToastType) => {
    const id = Math.random().toString(36).substring(7);
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const success = useCallback(
    (message: string | ReactNode) => showToast(message, 'success'),
    [showToast]
  );

  const error = useCallback(
    (message: string | ReactNode) => showToast(message, 'error'),
    [showToast]
  );

  const warning = useCallback(
    (message: string | ReactNode) => showToast(message, 'warning'),
    [showToast]
  );

  const info = useCallback(
    (message: string | ReactNode) => showToast(message, 'info'),
    [showToast]
  );

  const danger = useCallback(
    (message: string | ReactNode) => showToast(message, 'danger'),
    [showToast]
  );

  return (
    <ToastContext.Provider value={{ showToast, success, error, warning, info, danger }}>
      {children}
      <div className={styles.toastContainer}>
        {toasts.map((toast) => (
          <Toast
            key={toast.id}
            message={toast.message}
            type={
              toast.type === 'danger' ? 'error' : toast.type === 'warning' ? 'info' : toast.type
            }
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}
